import { AudioLines } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ShortVoiceEvidence } from '@/components/humint/voice/ShortVoiceEvidence'
import { Button } from '@/components/ui/button'
import { documentPath } from '@/features/documents/document-path'
import type { AudioEvidence } from './audio-evidence'

const COPY = 'investigations.entityDetail.audio'
type OpenDocument = ((documentId: string) => void) | null

function SourceAction({
  caseId,
  source,
  onOpenDocument,
}: {
  caseId: string
  source: NonNullable<AudioEvidence['source']>
  onOpenDocument: OpenDocument
}) {
  const { t } = useTranslation()
  const label = t('identity.detail.audio.openRecording')
  if (source.kind === 'document' && onOpenDocument)
    return (
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={() => onOpenDocument(source.documentId)}
      >
        <AudioLines aria-hidden />
        {label}
      </Button>
    )
  const to = source.kind === 'recording' ? source.to : documentPath(caseId, source.documentId)
  return (
    <Button variant="outline" size="xs" asChild>
      <Link to={to}>
        <AudioLines aria-hidden />
        {label}
      </Link>
    </Button>
  )
}

function VoiceStatus({ evidence }: { evidence: AudioEvidence }) {
  const { t, i18n } = useTranslation()
  const format = (value: number) =>
    new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(value)
  const seconds = evidence.speechSeconds === null ? null : format(evidence.speechSeconds)
  if (evidence.matchingStatus === 'insufficient_speech')
    return (
      <p className="text-xs text-muted-foreground">
        {seconds !== null && evidence.minimumSpeechSeconds !== null
          ? t(`${COPY}.insufficientSpeech`, {
              seconds,
              minimum: format(evidence.minimumSpeechSeconds),
            })
          : t(`${COPY}.insufficientSpeechUnknown`)}
      </p>
    )
  return (
    <>
      {seconds !== null && (
        <p className="text-xs text-muted-foreground">
          {t('identity.detail.audio.speech', { seconds })}
        </p>
      )}
      {evidence.matchingStatus === 'pending' && (
        <p className="text-xs text-muted-foreground">{t(`${COPY}.pending`)}</p>
      )}
      {evidence.shortEvidence && <ShortVoiceEvidence detail />}
    </>
  )
}

export function AudioEvidenceSection({
  caseId,
  evidence,
  clip,
  onOpenDocument,
}: {
  caseId: string
  evidence: AudioEvidence | null
  /** The voice's own player (`VoiceClipPlayer`), connected by the host; none for a recording. */
  clip?: ReactNode
  onOpenDocument: OpenDocument
}) {
  const { t } = useTranslation()
  if (!evidence) return null
  return (
    <section className="flex flex-col items-start gap-2 rounded-md border border-border/40 bg-card/40 p-2.5">
      <p className="text-xs font-medium">
        {evidence.kind === 'voice' && evidence.voice !== null
          ? t('investigations.entityDetail.passage.voice', { number: evidence.voice })
          : t(`${COPY}.${evidence.kind}`)}
      </p>
      {evidence.kind === 'voice' && <VoiceStatus evidence={evidence} />}
      {clip ? <div className="w-full">{clip}</div> : null}
      {evidence.source && (
        <SourceAction caseId={caseId} source={evidence.source} onOpenDocument={onOpenDocument} />
      )}
    </section>
  )
}
